// src/pages/Dashboard.jsx - Enhanced with campata modal to show cars in each position
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';
import {
  Car,
  MoreHorizontal,
  RotateCcw,
  Users,
  X
} from "lucide-react";
import { useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import ParkingStatus from "../components/dashboard/ParkingStatus";
import RecentActivities from "../components/dashboard/RecentActivities";
import StatsCard from "../components/dashboard/StatsCard";

// Colors for the charts
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6B7280'];

// Truncate long names for better mobile display
const truncateName = (name, maxLength = 15) => {
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength - 3) + '...';
};

// Modal component for showing cars in a campata
const CampataModal = ({ isOpen, onClose, campata, cars, campate, sezioni, tettoie, clienti }) => {
  if (!isOpen || !campata) return null;

  // Get full campata name
  const campataData = campate.find(c => c.id === campata.id);
  const sezione = sezioni.find(s => s.id === campataData?.sezione_id);
  const tettoia = tettoie.find(t => t.id === sezione?.tettoia_id);
  
  const fullName = tettoia && sezione 
    ? `${tettoia.nome} / ${sezione.nome} / ${campataData.nome}`
    : campataData?.nome || 'Posizione';

  // Filter cars in this campata
  const carsInCampata = cars.filter(car => car.campata_id === campata.id);

  // Helper to get client name
  const getClientName = (clienteId) => {
    const cliente = clienti.find(c => c.id === clienteId);
    return cliente ? `${cliente.nome} ${cliente.cognome}` : 'Cliente sconosciuto';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">{fullName}</h3>
            <p className="text-sm text-slate-600">{carsInCampata.length} auto parcheggiate</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {carsInCampata.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Car className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nessuna auto in questa posizione</p>
            </div>
          ) : (
            <div className="space-y-3">
              {carsInCampata.map((car) => (
                <div 
                  key={car.id} 
                  className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Cliente</p>
                      <p className="font-semibold text-blue-700">{getClientName(car.cliente_id)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Targa</p>
                      <p className="font-semibold text-slate-800">{car.targa || 'N/D'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Modello</p>
                      <p className="font-medium text-slate-700">
                        {car.marca && car.modello 
                          ? `${car.marca} ${car.modello}` 
                          : car.modello || car.marca || 'N/D'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Telaio</p>
                      <p className="font-mono text-sm text-slate-600">{car.numero_telaio || 'N/D'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

  // Modal component for showing all legend items
  const LegendModal = ({ isOpen, onClose, data, title }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-slate-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
          <div className="p-4 overflow-y-auto max-h-[60vh]">
            <div className="space-y-2">
              {data.map((entry, index) => (
                <div key={index} className="flex items-center justify-between p-2 rounded hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-sm flex-shrink-0" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-slate-700 font-medium">{entry.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-slate-800 font-semibold">{entry.value}</div>
                    <div className="text-slate-500 text-sm">{entry.percentage}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Compact Legend Component - shows only top 3 items
  const CompactLegend = ({ payload, onShowMore, showMoreButton = true }) => {
    const displayItems = payload.slice(0, 3);
    const remainingCount = payload.length - 3;

    return (
      <div className="mt-4">
        <div className="flex flex-wrap justify-center gap-2 text-sm">
          {displayItems.map((entry, index) => (
            <div key={index} className="flex items-center gap-1">
              <div 
                className="w-3 h-3 rounded-sm flex-shrink-0" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-slate-700">
                {truncateName(entry.value, 12)} ({entry.payload.percentage}%)
              </span>
            </div>
          ))}
          {showMoreButton && remainingCount > 0 && (
            <button
              onClick={onShowMore}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded text-sm transition-colors"
            >
              <MoreHorizontal className="w-3 h-3" />
              +{remainingCount} altri...
            </button>
          )}
        </div>
      </div>
    );
  };

// Enhanced Chart Card Component with modal legend
const ChartCard = ({ 
  title, 
  data, 
  isLoading, 
  dataKey = 'value', 
  nameKey = 'name',
  onSegmentClick = null,
  selectedSegment = null,
  showReset = false,
  onReset = null
}) => {
  const [showLegendModal, setShowLegendModal] = useState(false);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">{title}</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">{title}</h3>
        <div className="h-64 flex items-center justify-center text-slate-500">
          Nessun dato disponibile
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-3 border border-slate-200 rounded-lg shadow-lg z-50">
          <p className="font-medium text-slate-800">{data.name}</p>
          <p className="text-blue-600">
            <span className="font-semibold">{data.value}</span> auto ({data.payload.percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
          {showReset && selectedSegment && (
            <button
              onClick={onReset}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
          )}
        </div>
        
        {selectedSegment && (
          <div className="mb-3 text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded">
            Filtrato per: <span className="font-medium">{selectedSegment}</span>
          </div>
        )}
        
        {/* Chart Container - now with more space */}
        <div className="h-64 mb-2">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={80} // Slightly larger radius since we have more space
                paddingAngle={2}
                dataKey={dataKey}
                nameKey={nameKey}
                onClick={onSegmentClick}
                className={onSegmentClick ? "cursor-pointer" : ""}
              >
                {data.map((entry, index) => {
                  const isSelected = selectedSegment === entry.name;
                  return (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]}
                      stroke={isSelected ? "#1e40af" : "none"}
                      strokeWidth={isSelected ? 3 : 0}
                      style={{
                        filter: isSelected ? "brightness(1.1)" : "none",
                        opacity: onSegmentClick && !isSelected && selectedSegment ? 0.7 : 1
                      }}
                    />
                  );
                })}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        {/* Compact Legend - Desktop and Mobile */}
        <CompactLegend 
          payload={data.map((entry, index) => ({
            ...entry,
            color: COLORS[index % COLORS.length],
            value: entry.name,
            payload: entry
          }))}
          onShowMore={() => setShowLegendModal(true)}
          showMoreButton={data.length > 3}
        />
      </div>

      {/* Legend Modal */}
      <LegendModal
        isOpen={showLegendModal}
        onClose={() => setShowLegendModal(false)}
        data={data}
        title={`Dettagli: ${title}`}
      />
    </>
  );
};

// Export the enhanced ChartCard for use in your Dashboard
export { ChartCard };

// Helper function to process client data for chart
const processClientData = (cars, clients) => {
  const clientCounts = {};
  
  cars.forEach(car => {
    const clientId = car.cliente_id;
    if (clientCounts[clientId]) {
      clientCounts[clientId].count++;
    } else {
      const client = clients.find(c => c.id === clientId);
      const clientName = client ? `${client.nome} ${client.cognome}` : 'Cliente sconosciuto';
      clientCounts[clientId] = {
        name: clientName,
        count: 1,
        id: clientId // Store client ID for filtering
      };
    }
  });

  const total = cars.length;
  return Object.values(clientCounts).map(item => ({
    name: item.name,
    value: item.count,
    percentage: ((item.count / total) * 100).toFixed(1),
    clientId: item.id
  })).sort((a, b) => b.value - a.value);
};

// FIXED: Helper function to process brand data for chart (with optional client filter) with proper trimming
const processBrandData = (cars, selectedClientId = null) => {
  // Filter cars by client if specified
  const filteredCars = selectedClientId 
    ? cars.filter(car => car.cliente_id === selectedClientId)
    : cars;
  
  const brandCounts = {};
  
  filteredCars.forEach(car => {
    // FIXED: Trim whitespace and handle empty/null brands properly
    const rawBrand = car.marca || '';
    const brand = rawBrand.trim() || 'Non specificata';
    brandCounts[brand] = (brandCounts[brand] || 0) + 1;
  });

  const total = filteredCars.length;
  if (total === 0) return [];
  
  return Object.entries(brandCounts).map(([brand, count]) => ({
    name: brand,
    value: count,
    percentage: ((count / total) * 100).toFixed(1)
  })).sort((a, b) => b.value - a.value);
};

export default function Dashboard() {
  // State for interactive filtering
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState(null);
  
  // State for campata modal
  const [selectedCampata, setSelectedCampata] = useState(null);
  const [showCampataModal, setShowCampataModal] = useState(false);

  // Load real data from all tables
  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ['dashboard-data'],
    queryFn: async () => {
      console.log('Loading all dashboard data...')
      
      try {
        // Load all data in parallel - NOTE: Load ALL cars, not just 10 for accurate charts
        const [autoData, clientiData, statiData, tettoieData, sezioniData, campateData] = await Promise.all([
          supabase.from('auto').select('*').order('created_at', { ascending: false }), // Remove limit for charts
          supabase.from('clienti').select('*').order('nome'),
          supabase.from('stati').select('*').order('nome'),
          supabase.from('tettoie').select('*').order('nome'),
          supabase.from('sezioni').select('*').order('nome'),
          supabase.from('campate').select('*').order('nome')
        ]);

        // Check for errors
        const errors = [autoData.error, clientiData.error, statiData.error, tettoieData.error, sezioniData.error, campateData.error].filter(Boolean);
        if (errors.length > 0) {
          console.error('Supabase errors:', errors);
          throw new Error(`Database errors: ${errors.map(e => e.message).join(', ')}`);
        }

        const auto = autoData.data || [];
        const clienti = clientiData.data || [];
        const stati = statiData.data || [];
        const tettoie = tettoieData.data || [];
        const sezioni = sezioniData.data || [];
        const campate = campateData.data || [];

        console.log('Data loaded successfully:', {
          auto: auto.length,
          clienti: clienti.length,
          stati: stati.length,
          tettoie: tettoie.length,
          sezioni: sezioni.length,
          campate: campate.length
        });

        // Filter cars that have a position defined (campata_id is not null/undefined)
        const carsWithPosition = auto.filter(car => car.campata_id && car.campata_id !== null);
        
        console.log('Cars with position:', carsWithPosition.length, 'out of', auto.length, 'total cars');

        // Calculate stats - Use filtered cars for parking status
        const stats = {
          totalCars: auto.length, // Keep total cars as all registered cars
          totalClients: clienti.length,
          carsWithPosition: carsWithPosition.length // Add new stat for positioned cars
        };

        // Process chart data
        const clientChart = processClientData(auto, clienti);

        return {
          recentCars: auto.slice(0, 10), // Keep only 10 for recent activities
          allCars: auto, // Keep all cars for chart processing
          carsWithPosition, // NEW: Cars that have a position defined
          clienti,
          stati,
          tettoie,
          sezioni,
          campate,
          stats,
          clientChart
        };
      } catch (err) {
        console.error('Dashboard data loading error:', err);
        throw err;
      }
    },
    retry: 1,
    retryDelay: 1000,
    staleTime: 2 * 60 * 1000 // 2 minutes
  });

  // Extract data with fallbacks
  const {
    stats = {
      totalCars: 0,
      totalClients: 0,
      carsWithPosition: 0
    },
    recentCars = [],
    allCars = [],
    carsWithPosition = [], // NEW: Cars with position defined
    tettoie = [],
    sezioni = [],
    campate = [],
    stati = [],
    clienti = [],
    clientChart = []
  } = dashboardData || {};

  // Generate brand chart based on selected customer
  const brandChart = processBrandData(allCars, selectedCustomer);

  // Handlers for interactive filtering
  const handleCustomerClick = (data, index) => {
    const clientId = clientChart[index]?.clientId;
    if (clientId === selectedCustomer) {
      // Deselect if clicking the same customer
      setSelectedCustomer(null);
      setSelectedCustomerName(null);
    } else {
      // Select new customer
      setSelectedCustomer(clientId);
      setSelectedCustomerName(data.name);
    }
  };

  const handleResetFilter = () => {
    setSelectedCustomer(null);
    setSelectedCustomerName(null);
  };

  // Handler for campata click
  const handleCampataClick = (campata) => {
    setSelectedCampata(campata);
    setShowCampataModal(true);
  };

  const getClienteName = (clienteId) => {
    const cliente = clienti.find(c => c.id === clienteId);
    return cliente ? `${cliente.nome} ${cliente.cognome}` : "Cliente sconosciuto";
  };

  const getStatoName = (statoId) => {
    const stato = stati.find(s => s.id === statoId);
    return stato ? stato.nome : "Stato sconosciuto";
  };

  const getCampataFullName = (campataId) => {
    if (!campataId) return "N/D";
    const campata = campate.find(c => c.id === campataId);
    if (!campata) return "N/D";
    const sezione = sezioni.find(s => s.id === campata.sezione_id);
    if (!sezione) return campata.nome;
    const tettoia = tettoie.find(t => t.id === sezione.tettoia_id);
    if (!tettoia) return `${sezione.nome} / ${campata.nome}`;
    return `${tettoia.nome} / ${sezione.nome} / ${campata.nome}`;
  };

  // Error handling
  if (error) {
    return (
      <div className="p-4 md:p-8 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Dashboard</h1>
            <p className="text-slate-600">Panoramica generale del parcheggio</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-red-800 font-semibold mb-2">Errore nel caricamento dei dati</h3>
            <p className="text-red-600">
              Si è verificato un errore durante il caricamento dei dati della dashboard. 
              Controlla la connessione e riprova.
            </p>
            <p className="text-red-500 text-sm mt-2">{error.message}</p>
            <details className="mt-4">
              <summary className="cursor-pointer text-red-600 font-medium">Dettagli tecnici</summary>
              <pre className="mt-2 text-xs bg-red-100 p-2 rounded overflow-auto">
                {JSON.stringify(error, null, 2)}
              </pre>
            </details>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Dashboard</h1>
          <p className="text-slate-600">Panoramica generale del parcheggio</p>
        </div>

        {/* ENHANCED: Interactive Charts Section */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <ChartCard
            title="Distribuzione Auto per Cliente"
            data={clientChart}
            isLoading={isLoading}
            onSegmentClick={handleCustomerClick}
            selectedSegment={selectedCustomerName}
          />
          <ChartCard
            title={selectedCustomerName ? `Marche di ${selectedCustomerName}` : "Distribuzione Auto per Marca"}
            data={brandChart}
            isLoading={isLoading}
            showReset={!!selectedCustomer}
            onReset={handleResetFilter}
            selectedSegment={selectedCustomerName}
          />
        </div>

        {/* UPDATED: Stats Cards - Now only 2 cards instead of 3 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <StatsCard
            title="Auto Totali"
            value={stats.totalCars}
            icon={Car}
            color="blue"
            isLoading={isLoading}
          />
          <StatsCard
            title="Clienti"
            value={stats.totalClients}
            icon={Users}
            color="green"
            isLoading={isLoading}
          />
        </div>

        {/* EXISTING: Dashboard Components */}
        <div className="grid lg:grid-cols-2 gap-6">
          <RecentActivities
            cars={recentCars}
            getClienteName={getClienteName}
            getStatoName={getStatoName}
            getCampataFullName={getCampataFullName}
            isLoading={isLoading}
          />
          <ParkingStatus
            tettoie={tettoie}
            sezioni={sezioni}
            campate={campate}
            allCars={carsWithPosition}
            isLoading={isLoading}
            onCampataClick={handleCampataClick} // NEW: Pass click handler
          />
        </div>

        {/* Campata Modal */}
        <CampataModal
          isOpen={showCampataModal}
          onClose={() => setShowCampataModal(false)}
          campata={selectedCampata}
          cars={allCars}
          campate={campate}
          sezioni={sezioni}
          tettoie={tettoie}
          clienti={clienti}
        />
      </div>
    </div>
  );
}